import { useEffect, useState } from 'react'
import { Label, SearchField } from '@heroui/react'

import { FilterSelect } from '@/components/ui/filter-select'

import type { InventoryCategory, StockItemFilterValues, StockItemStatus } from '../types'
import { StockItemStatus as Status } from '../types'
import { itemStatusLabels } from './inventory-format'

interface StockItemFiltersProps {
  value: StockItemFilterValues
  onChange: (value: StockItemFilterValues) => void
  categories: InventoryCategory[]
  isDisabled?: boolean
}

/** How long the merchant has to stop typing before the list is asked again. */
const SEARCH_DEBOUNCE_MS = 300

/**
 * The stock list's filters, for the module bar.
 *
 * Status defaults to "Active and inactive" rather than "All": that is what the
 * API answers with no status at all, and archived items — soft-deleted ones —
 * only show when asked for by name. Calling the default "All" would promise rows
 * that are not there.
 */
export function StockItemFilters({
  value,
  onChange,
  categories,
  isDisabled,
}: StockItemFiltersProps) {
  // The box is typed into far faster than the list can answer, so it holds its own
  // value and only pushes one up once the typing settles.
  const [search, setSearch] = useState(value.search)
  const [lastPushed, setLastPushed] = useState(value.search)

  // A filter cleared elsewhere — the empty state's reset — has to reach the box too.
  // Adjusting during render rather than in an effect keeps it to a single pass.
  if (value.search !== lastPushed) {
    setLastPushed(value.search)
    setSearch(value.search)
  }

  useEffect(() => {
    if (search === value.search) return

    const timer = setTimeout(() => onChange({ ...value, search }), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [search, value, onChange])

  /* A fragment, not a row of its own: the panel lays out the row, with the
     products/ingredients switch beside these. No type filter — that switch is
     what decides the type. */
  return (
    <>
      <FilterSelect
        label="Status"
        allLabel="Active and inactive"
        triggerClassName="w-44"
        isDisabled={isDisabled}
        value={value.status}
        options={Object.values(Status).map((status) => ({
          value: status,
          label: itemStatusLabels[status],
        }))}
        onChange={(status) => onChange({ ...value, status: status as StockItemStatus | null })}
      />

      <FilterSelect
        label="Category"
        allLabel="All categories"
        triggerClassName="w-44"
        isDisabled={isDisabled}
        value={value.categoryId}
        options={categories.map((category) => ({ value: category.id, label: category.name }))}
        onChange={(categoryId) => onChange({ ...value, categoryId })}
      />

      <SearchField
        value={search}
        onChange={setSearch}
        isDisabled={isDisabled}
        aria-label="Search stock items"
        className="flex w-full flex-col gap-1.5 sm:w-64"
      >
        <Label className="sr-only">Search stock items</Label>
        <SearchField.Group>
          <SearchField.SearchIcon />
          <SearchField.Input placeholder="Name or SKU" />
          <SearchField.ClearButton />
        </SearchField.Group>
      </SearchField>
    </>
  )
}
